import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { authService, LoginRequest, UserData } from '../services/authService';

interface AuthContextType {
  user: UserData | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<UserData>) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  userRoles: string[];
  userPermissions: string[];
  hasPermission: (permission: string) => boolean;
  hasRole: (roleName: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = () => {
      const currentUser = authService.getCurrentUser();
      
      // If token is expired, clear everything
      if (!authService.isAuthenticated()) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      
      setUser(currentUser);
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    const response = await authService.login(credentials);
    setUser(response.user);
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const updateUser = (updates: Partial<UserData>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  // Show loading spinner while initializing auth state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        updateUser,
        isAuthenticated: !!user && authService.isAuthenticated(),
        isLoading,
        userRoles: authService.getUserRoles(),
        userPermissions: authService.getUserPermissions(),
        hasPermission: (permission: string) => authService.hasPermission(permission),
        hasRole: (roleName: string) => authService.hasRole(roleName),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};